// Add to your existing imports in services/api.ts

import qs from "qs";
import { Paginated, api } from "./api";
import { TrackerEntity } from "./trackerData";
import { Platform, PlatformFilter, MigrationStatus as MigrationStatusType } from "./platformSlice";

export interface TrackerParams {
    _id?: string;
    endpoint: TrackerEntity;
    entity?: any;
    includeDeleted?: boolean;
    platform?: PlatformFilter;
    migrationStatus?: MigrationStatusType;
}

export interface MigrationStatus {
    overall: number;
    campaigns: number;
    offers: number;
    publishers: number;
    lastUpdated: string;
}

export interface SyncResult {
    success: boolean;
    message: string;
    syncedEntities: {
        [key: string]: number;
    };
}

export interface ValidationResult {
    isValid: boolean;
    issues: string[];
    recommendations: string[];
}

export interface ComparisonResult {
    platform1: any;
    platform2: any;
    differences: string[];
    recommendations: string[];
}

export interface MigrationJob {
    id: string;
    entityType: string;
    entityIds: string[];
    status: MigrationStatusType;
    progress: number;
    startedAt: string;
    completedAt?: string;
    error?: string;
}

export interface MigrationHistory {
    id: string;
    entityType: string;
    entityCount: number;
    status: MigrationStatusType;
    startedAt: string;
    completedAt?: string;
    duration?: number;
}

export interface PlatformHealth {
    voluum: 'online' | 'offline' | 'degraded';
    redtrack: 'online' | 'offline' | 'degraded';
    lastChecked: string;
}

// Add new endpoints for the TrackerService within the api slice
export const trackerApi = api.injectEndpoints({
    endpoints: (builder) => ({
        findTrackerEntities: builder.query<any, TrackerParams>({
            query: (params) => ({
                url: `/tracker/?${qs.stringify(params)}`,
                method: 'GET',
            }),
        }),
        getTrackerEntity: builder.query<any, TrackerParams>({
            query: ({ endpoint, _id }) => ({
                url: `/tracker/${_id}?endpoint=${endpoint}`,
                method: 'GET',
            }),
        }),
        createTrackerEntity: builder.mutation<any, TrackerParams>({
            query: ({ endpoint, entity }) => ({
                url: `/tracker`,
                method: 'POST',
                body: { endpoint, entity },
            }),
        }),
        updateTrackerEntity: builder.mutation<any, TrackerParams & { id: string }>({
            query: ({ endpoint, entity, id, platform }) => ({
                url: `/tracker/${id}`,
                method: 'PUT',
                body: { endpoint, entity, platform },
            }),
        }),
        patchTrackerEntity: builder.mutation<any, TrackerParams & { id: string }>({
            query: ({ endpoint, entity, id, platform }) => ({
                url: `/tracker/${id}`,
                method: 'PATCH',
                body: { endpoint, entity, platform },
            }),
        }),
        deleteTrackerEntity: builder.query<{ id: string, status: string, items: { references: any[] }[] }, any>({
            query: (params) => ({
                url: `/tracker/${params.id}?${qs.stringify(params)}`, // Assuming offerId is used as the ID for deletion
                method: 'DELETE',
            }),
        }),
        
        // Migration-specific endpoints
        getMigrationStatus: builder.query<MigrationStatus, void>({
            query: () => '/migration/status',
        }),
        
        triggerSync: builder.mutation<SyncResult, { platform?: Platform }>({
            query: (params) => ({
                url: '/migration/sync',
                method: 'POST',
                body: params,
            }),
        }),
        
        validateEntity: builder.query<ValidationResult, { entityType: string; entityId: string }>({
            query: ({ entityType, entityId }) => `/migration/validate/${entityType}/${entityId}`,
        }),
        
        comparePlatforms: builder.query<ComparisonResult, { entityType: string; entityId: string }>({
            query: ({ entityType, entityId }) => `/platforms/compare/${entityType}/${entityId}`,
        }),
        
        startMigration: builder.mutation<MigrationJob, { entityType: string; entityIds: string[] }>({
            query: (params) => ({
                url: '/migration/start',
                method: 'POST',
                body: params,
            }),
        }),
        
        pauseMigration: builder.mutation<void, { jobId: string }>({
            query: ({ jobId }) => ({
                url: `/migration/${jobId}/pause`,
                method: 'POST',
            }),
        }),
        
        getMigrationHistory: builder.query<MigrationHistory[], void>({
            query: () => '/migration/history',
        }),
        

        
        // Add more tracker-specific endpoints as needed
    }),
});

export const {
    useFindTrackerEntitiesQuery,
    useGetTrackerEntityQuery,
    useCreateTrackerEntityMutation,
    useUpdateTrackerEntityMutation,
    usePatchTrackerEntityMutation,
    useDeleteTrackerEntityQuery,
    // Migration hooks
    useGetMigrationStatusQuery,
    useTriggerSyncMutation,
    useValidateEntityQuery,
    useComparePlatformsQuery,
    useStartMigrationMutation,
    usePauseMigrationMutation,
    useGetMigrationHistoryQuery,
    // Export other hooks as necessary
} = trackerApi;

export const {
    findTrackerEntities,
    getTrackerEntity,
    createTrackerEntity,
    updateTrackerEntity,
    patchTrackerEntity,
    deleteTrackerEntity,
    getMigrationStatus,
    triggerSync,
    validateEntity,
    comparePlatforms,
    startMigration,
    pauseMigration,
    getMigrationHistory,
} = trackerApi.endpoints;
